import * as H from "history";
import * as React from "react";
import { StickyContainer } from "react-sticky";
import { toast } from "react-toastify";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import styled, { css } from "styled-components";
import { Helmet } from "react-helmet";
import { capitalize } from "lodash";

import { breakpoint } from "src/library/utils/styled-media-queries/index";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import Grid from "src/library/components/Hedron";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import NotificationsFlyOut from "src/core/notifications/components/NotificationsFlyOut";
import { HeaderContext } from "src/core/shared/components/HeaderContext";
import { toggleNotifications } from "src/core/notifications/store/notifications.actions";
import { IStore } from "src/core/shared/store";
import {
  ContextType,
  MenuContext
} from "src/core/shared/hooks/menu/useMenu";
import {
  ILocationState,
  PageProps
} from "src/pages/Router/routes";
import {
  MobileMenuHeight,
  MenuWidth,
  QuickActionHeight
} from "src/context/SideBarContext/utils";

interface LayoutProps {
  children: React.ReactNode;
  allowedContexts?: ContextType[];
  title?: string | null;
  terminology: ITerminology;
  $noPadding?: boolean;
  loading?: boolean;
}

const Layout: React.ReactFCWithChildren<PageProps<LayoutProps, any>> = React.memo(props => {
  const [ { menuType, context } ] = React.useContext(MenuContext);

  const {
    children, history, terminology, location, loading, $noPadding
  } = props;

  const { error, showNotifications } = useMappedState(({ global, notifications }: IStore) => ({
    ...global,
    ...notifications,
    user: global.user
  }));

  const hasSideBar = !menuType ? false : menuType !== "NONE" && !error;
  const hasQuickAction = !!context[ 0 ] && [ "ORDER_PRODUCT_SELECTION", "LIST_PRODUCT_SELECTION" ].includes(context[ 0 ].type);
  const dispatch = useDispatch();
  const [ notificationFire, setNotificationFire ] = React.useState<boolean>(false);

  // On mount
  React.useEffect(() => {
    if (location && location.key && location.state && location.state.notification) {
      // Give the layout time to render before firing the notification
      setNotificationFire(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const locationState: H.Location<ILocationState> = { ...location };

    if (notificationFire) {
      if (showNotifications) {
        dispatch(toggleNotifications(false));
      }

      // If there is notification state, show it via toast
      if (location && location.key && location.state && location.state.notification) {
        switch (location.state.notification.type) {
          default:
          case "success":
            toast.success(location.state.notification.message);
            break;

          case "info":
            toast.info(location.state.notification.message);
            break;

          case "warn":
            toast.warn(location.state.notification.message);
            break;

          case "error":
            toast.error(location.state.notification.message);
            break;
        }

        // Delete notification from state once fired
        delete locationState.state.notification;

        // Replace the history with the updated state
        history.replace({
          ...history.location,
          ...locationState
        });
      }
    }
  }, [
    dispatch,
    history,
    location,
    notificationFire,
    showNotifications
  ]);

  return (
    <MainContainer
      className="main-container"
      direction="vertical"
    >
      <Helmet>
        <meta charSet="utf-8" />

        <title>
          {capitalize(props.title || "Mezze")}
        </title>
      </Helmet>

      {!!context.length && <HeaderContext context={context[ 0 ]} />}

      {showNotifications.show && (
        <NotificationsFlyOut
          onClose={() => dispatch(toggleNotifications(false, null))}
          terminology={terminology}
        />
      )}

      <MainWrapper
        id="main-wrapper"
        className={hasSideBar ? "sidebar-visible header-visible" : ""}
        $noPadding={$noPadding}
        $hasQuickAction={hasQuickAction}
      >

        {loading && (
          <LoadingSpinner
            className="loading-spinner"
            type="circular"
            position="inline"
            size={20}
          />
        )}

        <PageContent>
          {children}
        </PageContent>
      </MainWrapper>
    </MainContainer>
  );
});

const MainContainer = styled(Grid.Bounds)`
  ${({ theme }) => css`
    width: 100%;
    height: 100%;
    
    ${breakpoint("lg", theme)} {
      .main-content-inner + .info-bar-wrapper {
        max-width: 30%;
      }

      .main-content-inner {
       width: 10%;
      }
    }

    .header-context {
      display: none;

      ${breakpoint("md", theme)} {
        display: block;
      }
    }

    ${breakpoint("md", theme)} {
      width: calc(100vw - ${MenuWidth}px);
    }
  `}
  
`;

const MainWrapper = styled(StickyContainer)<{ $noPadding?: boolean; $hasQuickAction?: boolean }>`
  ${({
    theme,
    $noPadding,
    $hasQuickAction
  }) => css`
    left: 6rem;
    width: 100%;
    height: calc(100vh - ${MobileMenuHeight});
    margin-top: calc(${$hasQuickAction ? MobileMenuHeight + QuickActionHeight : MobileMenuHeight}px + ${theme.spacing.margins.lg});
    overflow-y: scroll;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    padding-left: ${$noPadding ? 0 : theme.spacing.padding.sm};
    padding-right: ${$noPadding ? 0 : theme.spacing.padding.sm};
    padding-top: ${theme.spacing.padding.sm};


    ${breakpoint("md", theme)} {
      padding-left: ${$noPadding ? 0 : theme.spacing.padding.lg};
      padding-right: ${$noPadding ? 0 : theme.spacing.padding.lg};
      height: 100vh;
      margin-top: ${theme.spacing.margins.lg};

      ${PageContent} {
        padding-bottom: ${theme.spacing.padding.lg};
      }
    }

  `};
`;

const PageContent = styled(Grid.Bounds)`
  ${({ theme }) => css`
    flex-direction: column-reverse;
    width: 100%;
    position: relative;
    flex-wrap: wrap;

    ${breakpoint("lg", theme)} {
      flex-direction: row;
    }
  `};
`;

export default Layout;
